import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  getLeadContacts, createLeadContact, updateLeadContact,
  createContact, updateContact, getContacts,
  getContactInfoTemplates, createContactInfoTemplate,
} from '../services/api';
import {
  Box, Typography, Button, TextField, Paper, Stack, Grid,
  Alert, CircularProgress, MenuItem, FormControlLabel, Checkbox,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

export default function ContactPersonPage() {
  const { parentId, contactId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const isLead = location.pathname.startsWith('/leads/');
  const isNew = !contactId || contactId === 'new';
  const backUrl = isLead ? `/leads/${parentId}?tab=contacts` : `/customers/${parentId}?tab=contacts`;

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Common fields
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [notes, setNotes] = useState('');
  const [isPrimary, setIsPrimary] = useState(false);

  // Lead-specific
  const [jobTitle, setJobTitle] = useState('');
  const [jobTitleConfirmed, setJobTitleConfirmed] = useState(false);
  const [linkedInUrl, setLinkedInUrl] = useState('');

  // Customer-specific
  const [designation, setDesignation] = useState('');

  // Shared: dynamic contact info fields
  const [contactInfos, setContactInfos] = useState([]);
  const [infoTemplates, setInfoTemplates] = useState([]);
  const [newTemplateName, setNewTemplateName] = useState('');

  const loadContact = useCallback(async () => {
    try {
      let contacts;
      if (isLead) {
        const { data } = await getLeadContacts(parentId);
        contacts = data;
      } else {
        const { data } = await getContacts(parentId);
        contacts = data;
      }
      const contact = (contacts || []).find(c => String(c.id) === String(contactId));
      if (!contact) { setError('Contact not found'); setLoading(false); return; }

      setFirstName(contact.firstName || '');
      setLastName(contact.lastName || '');
      setEmail(contact.email || '');
      setPhone(contact.phone || '');
      setNotes(contact.notes || '');
      setIsPrimary(contact.isPrimary || false);

      if (isLead) {
        setJobTitle(contact.jobTitle || '');
        setJobTitleConfirmed(!!contact.jobTitleConfirmed);
        setLinkedInUrl(contact.linkedInUrl || '');
      } else {
        setDesignation(contact.designation || '');
      }
      setContactInfos((contact.contactInfos || []).map(ci => ({
        contactInfoTemplateId: ci.contactInfoTemplateId,
        value: ci.value,
      })));
    } catch {
      setError('Failed to load contact');
    } finally {
      setLoading(false);
    }
  }, [parentId, contactId, isLead]);

  const loadInfoTemplates = useCallback(async () => {
    try {
      const { data } = await getContactInfoTemplates();
      setInfoTemplates(data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    if (!isNew) loadContact();
    loadInfoTemplates();
  }, [isNew, loadContact, loadInfoTemplates]);

  const handleSave = async () => {
    if (!firstName.trim() && !lastName.trim()) { setError('First or last name is required'); return; }
    setError('');
    setSaving(true);
    try {
      if (isLead) {
        const payload = { firstName: firstName.trim(), lastName: lastName.trim(), email: email.trim(), phone: phone.trim(), jobTitle: jobTitle.trim(), jobTitleConfirmed, linkedInUrl: linkedInUrl.trim(), isPrimary, notes: notes.trim(), contactInfos };
        if (isNew) {
          await createLeadContact(parentId, payload);
        } else {
          await updateLeadContact(parentId, contactId, payload);
        }
      } else {
        const payload = { firstName: firstName.trim(), lastName: lastName.trim(), designation: designation.trim(), email: email.trim(), phone: phone.trim(), notes: notes.trim(), isPrimary, contactInfos };
        if (isNew) {
          await createContact(parentId, payload);
        } else {
          await updateContact(parentId, contactId, payload);
        }
      }
      navigate(backUrl);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save contact');
    } finally {
      setSaving(false);
    }
  };

  // Contact info row helpers
  const addContactInfoRow = () => {
    if (infoTemplates.length === 0) return;
    setContactInfos([...contactInfos, { contactInfoTemplateId: infoTemplates[0].id, value: '' }]);
  };

  const removeContactInfoRow = (index) => {
    const infos = [...contactInfos];
    infos.splice(index, 1);
    setContactInfos(infos);
  };

  const updateContactInfoRow = (index, field, value) => {
    const infos = [...contactInfos];
    infos[index] = { ...infos[index], [field]: field === 'contactInfoTemplateId' ? parseInt(value) : value };
    setContactInfos(infos);
  };

  const handleAddTemplate = async () => {
    if (!newTemplateName.trim()) return;
    try {
      await createContactInfoTemplate({ fieldName: newTemplateName.trim(), icon: '' });
      setNewTemplateName('');
      loadInfoTemplates();
    } catch { setError('Failed to create template'); }
  };

  if (loading) return <Box sx={{ p: 5, textAlign: 'center' }}><CircularProgress /></Box>;

  return (
    <Box>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(backUrl)} sx={{ mb: 2 }}>
        Back to {isLead ? 'Lead' : 'Customer'}
      </Button>

      <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>{isNew ? 'New Contact' : 'Edit Contact'}</Typography>
          <Typography variant="body2" color="text.secondary">
            Manage contact person details{isLead ? ' for this lead' : ' for this customer'}.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" onClick={() => navigate(backUrl)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : isNew ? 'Create Contact' : 'Save Changes'}
          </Button>
        </Stack>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Basic Information */}
      <Paper sx={{ p: 3, mb: 2.5 }}>
        <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="h6">👤 Basic Information</Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Name and role of the contact person.</Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField size="small" fullWidth label={`First Name${!isLead ? ' *' : ''}`} value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="First name" />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField size="small" fullWidth label={`Last Name${!isLead ? ' *' : ''}`} value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Last name" />
          </Grid>
        </Grid>
        {isLead ? (
          <>
            <TextField size="small" fullWidth label="Job Title" value={jobTitle} onChange={(e) => setJobTitle(e.target.value)} placeholder="e.g. CTO, VP of Sales" sx={{ mt: 2 }} />
            <FormControlLabel
              control={<Checkbox checked={jobTitleConfirmed} onChange={(e) => setJobTitleConfirmed(e.target.checked)} />}
              label="Job title confirmed (verified directly with the contact)"
              sx={{ mt: 0.5 }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: -0.5, ml: 4 }}>
              When unchecked, AI personalization will not use this title (avoids stale or scraped data leaking into outreach).
            </Typography>
          </>
        ) : (
          <TextField size="small" fullWidth label="Designation" value={designation} onChange={(e) => setDesignation(e.target.value)} placeholder="e.g. Manager, Director" sx={{ mt: 2 }} />
        )}
        <FormControlLabel
          control={<Checkbox checked={isPrimary} onChange={(e) => setIsPrimary(e.target.checked)} />}
          label="Primary Contact"
          sx={{ mt: 1 }}
        />
      </Paper>

      {/* Contact Details */}
      <Paper sx={{ p: 3, mb: 2.5 }}>
        <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="h6">📞 Contact Details</Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Email, phone, and other ways to reach this person.</Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField size="small" fullWidth type="email" label="Email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="email@example.com" />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField size="small" fullWidth label="Phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+1-555-0100" />
          </Grid>
        </Grid>
        {isLead && (
          <TextField size="small" fullWidth label="LinkedIn URL" value={linkedInUrl} onChange={(e) => setLinkedInUrl(e.target.value)} placeholder="https://linkedin.com/in/..." sx={{ mt: 2 }} />
        )}

        {/* Dynamic contact detail fields */}
        {contactInfos.map((info, idx) => (
          <Stack key={idx} direction="row" spacing={1} sx={{ alignItems: 'flex-end', mt: 1 }}>
            <TextField
              select
              size="small"
              label={idx === 0 ? 'Field Type' : undefined}
              value={info.contactInfoTemplateId}
              onChange={(e) => updateContactInfoRow(idx, 'contactInfoTemplateId', e.target.value)}
              sx={{ width: 180, minWidth: 140, flexShrink: 0 }}
            >
              {infoTemplates.map((t) => <MenuItem key={t.id} value={t.id}>{t.fieldName}</MenuItem>)}
            </TextField>
            <TextField
              size="small"
              fullWidth
              label={idx === 0 ? 'Value' : undefined}
              value={info.value}
              onChange={(e) => updateContactInfoRow(idx, 'value', e.target.value)}
              placeholder="Enter value..."
            />
            <Button variant="outlined" color="error" size="small" onClick={() => removeContactInfoRow(idx)} sx={{ whiteSpace: 'nowrap' }}>
              Remove
            </Button>
          </Stack>
        ))}

        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mt: contactInfos.length > 0 ? 1 : 2 }}>
          <Button variant="outlined" size="small" onClick={addContactInfoRow} disabled={infoTemplates.length === 0}>
            + Add Detail
          </Button>
          <TextField
            size="small"
            fullWidth
            placeholder="New field type (e.g. WhatsApp, Skype...)"
            value={newTemplateName}
            onChange={(e) => setNewTemplateName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddTemplate(); } }}
          />
          <Button variant="outlined" size="small" onClick={handleAddTemplate}>Create Type</Button>
        </Stack>
      </Paper>

      {/* Notes */}
      <Paper sx={{ p: 3, mb: 2.5 }}>
        <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="h6">📝 Notes</Typography>
        </Stack>
        <TextField
          size="small"
          fullWidth
          multiline
          rows={3}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Any notes about this contact..."
        />
      </Paper>

      {/* Bottom save bar */}
      <Paper elevation={3} sx={{ position: 'sticky', bottom: 0, p: 2, mt: 3, borderTop: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        <Button variant="outlined" onClick={() => navigate(backUrl)}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : isNew ? 'Create Contact' : 'Save Changes'}
        </Button>
      </Paper>
    </Box>
  );
}
